/**
 * SyncStatusChip
 *
 * Compact chip that shows the live PowerSync connection state.
 * Uses `useSyncStatus` to subscribe to main-process push events.
 *
 * States:
 *   connected + uploadQueueSize > 0  → "Syncing…"    (amber)
 *   connected                        → "Online"       (green)
 *   connecting (TCP handshake)       → "Connecting…"  (amber, pulsing)
 *   disconnected / initial           → "Offline"      (red)
 *
 * When offline, a Reconnect button appears so the user is never stuck.
 * An automatic 30-second retry also runs silently in the background.
 */

import { useSyncStatus } from '@hooks/useSyncStatus';
import { useAppSelector } from '@hooks/useRedux';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';

import { selectCurrentUser } from '@/renderer/store/auth/authSelectors';
import { ensureOutletSync } from '@/renderer/lib/powersync/ensureOutletSync';

type Variant = 'online' | 'syncing' | 'connecting' | 'offline';

interface ChipConfig {
  label: string;
  dotClass: string;
  textClass: string;
  bgClass: string;
  borderClass: string;
  icon: React.ReactNode;
}

const CONFIGS: Record<Variant, ChipConfig> = {
  online: {
    label: 'Online',
    dotClass: 'bg-emerald-500',
    textClass: 'text-emerald-700',
    bgClass: 'bg-emerald-50',
    borderClass: 'border-emerald-200',
    icon: <Wifi size={12} className="text-emerald-600" />,
  },
  syncing: {
    label: 'Syncing…',
    dotClass: 'bg-amber-400 animate-pulse',
    textClass: 'text-amber-700',
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-200',
    icon: <Wifi size={12} className="text-amber-600" />,
  },
  connecting: {
    label: 'Connecting…',
    dotClass: 'bg-amber-400 animate-pulse',
    textClass: 'text-amber-700',
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-200',
    icon: <Wifi size={12} className="text-amber-600" />,
  },
  offline: {
    label: 'Offline',
    dotClass: 'bg-red-500',
    textClass: 'text-red-700',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    icon: <WifiOff size={12} className="text-red-600" />,
  },
};

/** 30 seconds between automatic reconnect attempts while offline */
const AUTO_RECONNECT_INTERVAL_MS = 30_000;

export const SyncStatusChip: React.FC = () => {
  const { connected, connecting, uploadQueueSize, hasSynced } = useSyncStatus();
  const currentUser = useAppSelector(selectCurrentUser);
  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  let variant: Variant;
  if (connecting && !connected) {
    // TCP handshake in progress — not offline, not yet connected
    variant = 'connecting';
  } else if (!connected) {
    variant = 'offline';
  } else if (uploadQueueSize > 0) {
    variant = 'syncing';
  } else {
    variant = 'online';
  }

  const isOffline = variant === 'offline';

  const handleReconnect = useCallback(() => {
    console.info('[PowerSync] Manual reconnect requested');
    ensureOutletSync(currentUser?.outlet_id);
  }, [currentUser?.outlet_id]);

  // Automatically retry every 30 seconds while offline — including before
  // the very first sync ever completes, not just mid-session disconnections,
  // since a stuck-before-first-sync outlet is exactly the "user is never
  // stuck just because of PowerSync" case this must cover.
  useEffect(() => {
    if (isOffline) {
      retryTimerRef.current = setInterval(() => {
        console.info('[PowerSync] Auto-reconnect attempt…');
        ensureOutletSync(currentUser?.outlet_id);
      }, AUTO_RECONNECT_INTERVAL_MS);
    }

    return () => {
      if (retryTimerRef.current !== null) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [isOffline, currentUser?.outlet_id]);

  const config = CONFIGS[variant];

  return (
    <div className="flex items-center gap-1.5">
      <div
        title={hasSynced ? `PowerSync: ${variant}` : 'Waiting for first sync…'}
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium leading-none select-none ${config.bgClass} ${config.borderClass} ${config.textClass}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${config.dotClass}`} />
        {config.icon}
        <span>{config.label}</span>
      </div>

      {/* Manual reconnect button — only shown when offline */}
      {isOffline && (
        <button
          type="button"
          onClick={handleReconnect}
          title="Reconnect PowerSync"
          className="flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-1 text-[11px] font-medium leading-none text-red-700 transition-colors hover:bg-red-100 active:scale-95"
        >
          <RefreshCw size={10} />
          <span>Reconnect</span>
        </button>
      )}
    </div>
  );
};
